import {
  setConnectionsOrderBy,
  useConnectionsTableActions,
} from '@mm/state/modules/connectionsTableState/actions';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { GridCell } from '../../../../../common/components/Grid/Grid';
import { colors } from '../../../beta/styles/colors';
import { ConnectionsPanel } from '../../panels/ConnectionsPanel/ConnectionsPanel';
import { Status, ThreeDotMenu } from '@mm/components';
import './ConnectionsTableGrid.scss';
import { EditOutlined } from '@ant-design/icons';
import ConnectionModel, {
  FormattedConnectionModel,
} from '@mm/state/model/ConnectionModel';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useFetchConnections } from '../../../beta/modules/Integrations/use-fetch-connections';

type CellProps = { item: FormattedConnectionModel };

const renderDataCell = (field: string) => (item: CellProps) =>
  (
    <GridCell className="connections-table__grid-cell">
      {item[field] as string}
    </GridCell>
  );

const renderNameCell = (
  item: FormattedConnectionModel,
  onEditConnection: (item: FormattedConnectionModel) => void,
) => (
  <GridCell className="name-cell" onClick={() => onEditConnection(item)}>
    {item.name}
  </GridCell>
);

const renderStatusCell = ({ item }: CellProps) => (
  <GridCell>
    <Status
      status={item.active ? 'active' : 'draft'}
      color={!item.active ? colors.grayLight : undefined}
    />
  </GridCell>
);

const renderActionsCell = (
  item: FormattedConnectionModel,
  onEditConnection: (id: FormattedConnectionModel) => void,
  onDeleteConnection: (item: FormattedConnectionModel) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return onDeleteConnection(item);
      default:
        break;
    }
  };

  const elements = [{ name: 'Delete', value: 'delete' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <GridCell className="action-cell">
      <EditOutlined
        onClick={() => onEditConnection(item)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </GridCell>
  );
};

const generateTableCells = (
  onEditConnection: (item: FormattedConnectionModel) => void,
  onDeleteConnection: (item: FormattedConnectionModel) => void,
  onChangeOrder: (orderBy: string) => void,
) => {
  const cells = [
    {
      label: 'Name',
      keyName: 'name',
      rowClassName: 'name-cell',
      onChange: onChangeOrder,
      Cell: (props: CellProps) => renderNameCell(props.item, onEditConnection),
    },
    {
      label: 'Description',
      keyName: 'description',
      onChange: setConnectionsOrderBy,
      renderCell: renderDataCell('description'),
    },
    {
      label: 'Created date',
      onChange: setConnectionsOrderBy,
      keyName: 'formattedCreatedAt',
      renderCell: renderDataCell('formattedCreatedAt'),
    },
    {
      label: 'Updated date',
      onChange: setConnectionsOrderBy,
      keyName: 'formattedUpdatedAt',
      renderCell: renderDataCell('formattedUpdatedAt'),
    },
    {
      label: 'Status',
      keyName: 'statusText',
      onChange: onChangeOrder,
      Cell: renderStatusCell,
    },
    {
      label: 'Actions',
      keyName: 'actions',
      Cell: ({ item }: CellProps) =>
        renderActionsCell(item, onEditConnection, onDeleteConnection),
    },
  ];
  return cells;
};

const ConnectionsTableGrid = () => {
  const connectionsCRUDActions = useConnectionCRUDActions();
  const { setConnectionsOrderBy, setActiveIntegration } =
    useConnectionsTableActions();
  const connectedPageId = useSelector(activeFacebookPageIdSelector);
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal } = useModalActions();
  const { fetch } = useFetchConnections();

  useEffect(() => {
    if (connectedPageId) {
      fetch(1, 200, undefined);
    }
  }, [fetch, connectedPageId]);

  const onDeleteConnection = (connection: ConnectionModel) => {
    const deleteCallback = async () => {
      connectionsCRUDActions.deleteConnection(connectedPageId, connection);
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${connection.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };

  const onChangeOrder = orderBy => {
    setConnectionsOrderBy(orderBy);
  };

  const onEditConnection = (connection: ConnectionModel) => {
    showSlideoutMenu('editJSONWebhookIntegrationEditor', {
      webhookId: connection.id,
    });
  };

  const data = generateTableCells(
    onEditConnection,
    onDeleteConnection,
    onChangeOrder,
  );

  return <ConnectionsPanel cellData={data} />;
};

export default ConnectionsTableGrid;
